import { z } from 'zod'
import { InstantieId, MessageId, SectionId, StempelId, ThreadId } from './id.js'
import { Laag } from './laag.js'
import { Moment, Person } from './moment.js'

/**
 * Het stempelregister van de opmerkingen (besluit van 2 november 2026, eisenboek 12.46, ontwerp 6.8, R-73, R-154).
 *
 * De pod stempelt een bericht met wie het schreef en wanneer, en een draadje met wie het oploste of
 * heropende. Tot 2 november 2026 stond die stempel alleen in het sectiedocument, en dat had twee gaten.
 * Een client die een nieuw bericht met een eigen `persoon` en `moment` stuurde, kwam langs de stempeling,
 * want de pod stempelde alleen wat nog geen auteur had. En de stempel opnieuw zetten bij elke wijziging kan
 * niet: na een crash van de pod zonder wegschrijven stuurt een browser die opnieuw verbindt de berichten van
 * anderen mee, en die zouden dan zijn naam krijgen.
 *
 * Het register lost beide op. Een regel staat in de opslag **voordat** de stempel in het levende document
 * staat; wat een browser ooit als gestempeld heeft gezien, staat dus altijd hier. Bij elke wijziging van
 * een client houdt een bericht waarvan het id hier staat de persoon en het moment van het register, en
 * krijgt een bericht dat hier niet staat en ook niet in de opgeslagen stand de persoon van wie het stuurde.
 *
 * **Append-only.** Een regel wordt nooit gewijzigd. Voor een bericht is de eerste regel de waarheid; voor
 * oplossen en heropenen geldt de nieuwste regel van het draadje.
 *
 * **De tekst als hash, niet als tekst.** Het id van een bericht kiest de client, en iedereen die meeleest
 * kent het. Zonder hash kon een client een nieuwe map met het id van een ander bericht en een eigen tekst
 * zetten, en die kreeg dan de naam uit het register. Met de hash geldt de regel alleen voor het bericht met
 * die tekst; de tekst zelf staat al in het document en hoeft niet nog een keer in de opslag.
 */
export const StempelSoort = z.enum(['bericht', 'opgelost', 'heropend'])
export type StempelSoort = z.infer<typeof StempelSoort>

/** De SHA-256 van de tekst van een bericht, hexadecimaal. */
export const TekstHash = z.string().regex(/^[0-9a-f]{64}$/, 'een SHA-256 in kleine hexadecimale tekens')
export type TekstHash = z.infer<typeof TekstHash>

/** Wat de pod aanbiedt. Het `id` maakt hij zelf, zodat een herhaald verzoek na een time-out niets verdubbelt. */
export const StempelRequest = z
  .strictObject({
    id: StempelId,
    soort: StempelSoort,
    draadId: ThreadId,
    /** Bij een bericht het id, anders niets. */
    berichtId: MessageId.optional(),
    /** Bij een bericht de hash van zijn tekst, anders niets. */
    tekstHash: TekstHash.optional(),
    persoon: Person,
    moment: Moment,
  })
  .superRefine((request, ctx) => {
    const message = request.soort === 'bericht'
    if (message !== (request.berichtId !== undefined) || message !== (request.tekstHash !== undefined)) {
      ctx.addIssue({ code: 'custom', path: ['berichtId'], message: 'een stempel van een bericht noemt het bericht en de hash van zijn tekst; een stempel van een draadje geen van beide' })
    }
  })
export type StempelRequest = z.infer<typeof StempelRequest>

/** Een regel zoals de opslag hem bewaart en teruggeeft. */
export const Stempel = z.strictObject({
  id: StempelId,
  instantieId: InstantieId,
  laag: Laag,
  sectieId: SectionId,
  soort: StempelSoort,
  draadId: ThreadId,
  berichtId: MessageId.nullable(),
  tekstHash: TekstHash.nullable(),
  persoon: Person,
  moment: Moment,
})
export type Stempel = z.infer<typeof Stempel>
